import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const EditBranchModal = ({ open, handleClose, branchData, handleSave }) => {
  const [formData, setFormData] = useState({
    phone: "",
    name: "",
    address: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (branchData) {
      setFormData({ ...branchData });
    } else {
      setFormData({ phone: "", name: "", address: "" });
    }
    setErrors({});
  }, [branchData, open]); 

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      // Allow only digits and max 10 characters
      if (!/^\d*$/.test(value)) return;
      if (value.length > 10) return;
    }

    setFormData(prevData => ({ ...prevData, [name]: value }));
    setErrors(prevErrors => ({ ...prevErrors, [name]: "" }));
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone number must be exactly 10 digits";
    }
    
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    
    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(() => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length === 0) {
      handleSave(formData);
      handleClose();
    } else {
      setErrors(validationErrors);
    }
  }, [formData, validate, handleSave, handleClose]);

  const handleModalClose = useCallback(() => {
    setErrors({});
    handleClose();
  }, [handleClose]);

  const modalStyles = useMemo(() => ({
    style: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: 650,
      width: "90%",
      bgcolor: "background.paper",
      borderRadius: 1,
      boxShadow: 24,
      p: { xs: "20px", sm: "34px" },
    },
    modalTitle: {
      color: "#00A651",
    },
    cancelBtn: {
      backgroundColor: "white",
      border: "1px solid green",
      color: "green",
    },
    saveBtn: {
      backgroundColor: "green",
      border: "1px solid green",
      color: "white",
    },
  }), []);

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalStyles.style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: "20px",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            style={modalStyles.modalTitle}
          >
            {branchData ? "Edit Branch" : "Add New Branch"}
          </Typography>

          <IconButton onClick={handleModalClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          label="Name"
          variant="outlined"
          name="name"
          value={formData.name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.name}
          helperText={errors.name}
        />
        <TextField
          label="Phone"
          variant="outlined"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.phone}
          helperText={errors.phone}
          inputProps={{ maxLength: 10, inputMode: "numeric" }}
        />

        <TextField
          label="Address"
          variant="outlined"
          name="address"
          value={formData.address}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.address}
          helperText={errors.address}
        />
        
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "10px", mt: 2 }}>
          <Button
            variant="contained"
            style={modalStyles.cancelBtn}
            onClick={handleModalClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            style={modalStyles.saveBtn}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

EditBranchModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  branchData: PropTypes.object,
  handleSave: PropTypes.func.isRequired,
};

export default React.memo(EditBranchModal);

const webStyle = {
  style: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxWidth: 650,
    width: "90%",
    bgcolor: "background.paper",
    borderRadius: 1,
    boxShadow: 24,
    // p: 4,
    p: { xs: "20px", sm: "34px" },
  },
  modalTitle: {
    color: "#00A651",
  },
  cancelBtn: {
    backgroundColor: "white",
    border: "1px solid green",
    color: "green",
  },
  saveBtn: {
    backgroundColor: "green",
    border: "1px solid green",
    color: "white",
  },
};
